import {
  set as setLocalStorage,
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import {
  dateIsBefore,
  intlDateFormatter,
  intlHyphenDateRangeFormatter,
} from 'lib/datetime/dateUtils'

import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DEFAULT_FLEXIBLE_DURATION_RANGE, HOTELS_ENABLED_FILTERS, RECENT_SEARCHES_LIMIT, SEARCH_STORAGE_KEYS, WORLD_TRAVEL_DAY_2026_CAMPAIGN_SLUG } from 'constants/search'

import type { EnabledFilters, RecentDate, RecentFilters, RecentFlexibleSearch } from './type'
import type { IntlShape } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import moment from 'moment'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { take, unique, uniqueBy } from 'lib/array/arrayUtils'
import config from 'constants/config'
import {
  INTL_DAY_ONLY,
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
  INTL_SHORT_MONTH,
} from 'constants/dateFormats'
import { prettyFlexibleDates } from './SearchForm/SearchDateInput/FlexibleDateSearchField'
import { LOYALTY_SEARCH_FILTER_VALUES } from 'loyalty/constants/loyaltySearchFilters'
import { isBedbankOffer, isExperienceOffer, isLEHotel } from 'lib/offer/offerTypes'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import { getSearchItemExperienceId } from 'lib/search/searchItem'

export const searchPaneInteractEventName = 'search-container-pane-interact'

export function formatLocationsDisplayText(intl: IntlShape, searchItem?: App.SearchItem, placeholder?: string) {
  if (!searchItem) {
    return placeholder || intl.formatMessage({
      id: '_m9qMkKnX3ezvNt4Pfee',
      defaultMessage: 'Search for a destination or hotel',
      description: 'Default placeholder text for search location input',
    })
  }

  return searchItem.format.mainText
}

export function getRoomsCountText(intl: IntlShape, rooms: Array<App.Occupants>) {
  return intl.formatMessage(
    {
      id: 'E0vsEAR7Hn7TaTFIHZbg',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count label in search occupancy summary in the search interface',
    },
    { count: rooms.length },
  )
}

export function getTotalRoomsString(intl: IntlShape, rooms: Array<App.Occupants>, saleUnit?: string) {
  if (saleUnit) {
    const saleUnitText = formatSaleUnitCount(intl, saleUnit, rooms.length)
    return `(${saleUnitText})`
  }
  return `(${getRoomsCountText(intl, rooms)})`
}

export function getChildrenAgesString(intl: IntlShape, rooms: Array<App.Occupants>) {
  const allChildren = rooms.flatMap(room => room.childrenAge)
  const ageLabels = allChildren.map(age =>
    intl.formatMessage(
      {
        id: 'cJEvB8YRY5ZLVBUrv33k',
        defaultMessage: '{age}yo',
        description: 'Child age abbreviation (years old) in search occupancy',
      },
      { age },
    ),
  )
  return ageLabels.length > 0 ? `(${ageLabels.join(', ')})` : ''
}

export function saveRecentRooms(rooms: Array<Array<App.Occupants>>) {
  setLocalStorage(SEARCH_STORAGE_KEYS.TRAVELLERS, rooms)
}

export function loadRecentRooms(): Array<Array<App.Occupants>> {
  return getLocalStorage(SEARCH_STORAGE_KEYS.TRAVELLERS) ?? []
}

export function saveLastDates(dates: RecentDate) {
  setLocalStorage(SEARCH_STORAGE_KEYS.LAST_DATES, dates)
}

export function saveLastFlexibleSelection(flexibleSelection: RecentFlexibleSearch) {
  setLocalStorage(SEARCH_STORAGE_KEYS.FLEXIBLE_OPTIONS, flexibleSelection)
}

export function clearLastDates() {
  setLocalStorage(SEARCH_STORAGE_KEYS.LAST_DATES, null)
}

/**
 * This function does not consider the destination's time zone. Do not use this function if you're
 * validating the check-in date for a _specific_ property; consider `isAccommodationCheckInDatePast`
 * instead.
 */
export function isTodayBeforeCheckin(checkin: string) {
  const today = new Date()
  return !dateIsBefore(new Date(checkin), today)
}

export function isTodayBeforeFlexibleMonth(flexibleMonth: string) {
  const flexibleMonthArray = flexibleMonth.split(',')
  return flexibleMonthArray.some((month) => {
    const momentMonth = moment(month)
    return momentMonth.isAfter()
  })
}

export function isRecentSearchWithinTimeLimit(dateAdded: string, limit: number, unit: moment.unitOfTime.DurationConstructor) {
  const dateAddedMoment = moment(dateAdded)
  const now = moment()
  const diff = now.diff(dateAddedMoment, unit)
  return diff <= limit
}

export function getCheckInCheckOutText(checkIn: string, checkOut: string): string {
  const intl = getCurrentIntl()
  const checkInDate = moment(checkIn, 'YYYY-MM-DD')
  const checkOutDate = moment(checkOut, 'YYYY-MM-DD')

  if (!checkInDate.isValid() || !checkOutDate.isValid()) {
    return ''
  }

  return intlHyphenDateRangeFormatter(checkInDate, checkOutDate, intl.locale, false)
}

function formatFlexibleMonthLabel(intl: IntlShape, flexibleMonth: string): string {
  const [year, month] = flexibleMonth.split('-')

  if (!year || !month) {
    return flexibleMonth
  }

  return intlDateFormatter(
    `${year}-${month.padStart(2, '0')}-01`,
    INTL_SHORT_MONTH,
    intl.locale,
  )
}

function getFlexibleMonthsText(intl: IntlShape, flexibleMonths: string): string {
  const months = flexibleMonths.split(',').filter(Boolean)
  const monthLabels = months.map(month => formatFlexibleMonthLabel(intl, month))

  if (monthLabels.length === 0) {
    return ''
  }

  if (monthLabels.length === 1) {
    return monthLabels[0]
  }

  if (monthLabels.length === 2) {
    return intl.formatMessage(
      {
        id: 'C7Zvy0LyjOMdTpRcjoHp',
        defaultMessage: '{firstMonth} & {secondMonth}',
        description: 'Flexible month summary showing two selected months in compact search copy',
      },
      {
        firstMonth: monthLabels[0],
        secondMonth: monthLabels[1],
      },
    )
  }

  return intl.formatMessage(
    {
      id: 'X_ZkbTN5V9u4orIH-G1k',
      defaultMessage: '{firstMonth} & {secondMonth} + {remainingMonthCount}',
      description: 'Flexible month summary showing the first two selected months and the number of extra months in compact search copy',
    },
    {
      firstMonth: monthLabels[0],
      secondMonth: monthLabels[1],
      remainingMonthCount: monthLabels.length - 2,
    },
  )
}

export function getFlexibleDurationAndMonthText(intl: IntlShape, flexibleDuration: DEFAULT_FLEXIBLE_DURATION_RANGE, flexibleMonths: string): string {
  // Build duration and month summaries separately so the final branch can
  // still return one localized phrase without manual English glue.
  const durationLabel =
    flexibleDuration && flexibleDuration !== DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION ?
        intl.formatMessage(
          {
            id: 'aMCO3c2J5iEO2V6Bkc6D',
            defaultMessage: '{minDays} - {maxDays} nights',
            description: 'Flexible duration range label showing the minimum and maximum number of nights',
          },
          {
            minDays: flexibleDuration.split('-')[0],
            maxDays: flexibleDuration.split('-')[1],
          },
        ) :
      ''

  const flexibleMonthsLabel = flexibleMonths ? getFlexibleMonthsText(intl, flexibleMonths) : ''

  if (durationLabel && flexibleMonthsLabel) {
    return intl.formatMessage(
      {
        id: 'fihgJApCkANaES87G6_H',
        defaultMessage: '{durationLabel}, {monthsLabel}',
        description: 'Flexible search summary showing both the selected night range and selected months',
      },
      {
        durationLabel,
        monthsLabel: flexibleMonthsLabel,
      },
    )
  }

  return durationLabel || flexibleMonthsLabel
}

export function getRecentSearchDescription(
  intl: IntlShape,
  checkInDate?: string,
  checkOutDate?: string,
  flexibleMonths?: string,
  flexibleDuration?: DEFAULT_FLEXIBLE_DURATION_RANGE,
  isAnytime?: boolean,
): string {
  if (isAnytime) {
    return intl.formatMessage({
      id: '7iPZ9ybbzFRJQGcKgPmd',
      defaultMessage: 'Anytime',
      description: 'Label for flexible date search with no specific dates',
    })
  }

  if (checkInDate && checkOutDate) {
    return getCheckInCheckOutText(checkInDate, checkOutDate)
  }

  if (flexibleMonths || flexibleDuration) {
    return getFlexibleDurationAndMonthText(intl, flexibleDuration ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION, flexibleMonths ?? '')
  }

  return ''
}

const emptyArray: Array<App.FullRecentSearch> = []
const emptyStringArray: Array<string> = []

function getRecentSearchesStorageKey(userKey?: string) {
  const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
  if (shouldUseMemberId) return userKey
  return userKey ?? SEARCH_STORAGE_KEYS.RECENT_SEARCHES
}

function getRemovedRecentSearchesStorageKey(userKey?: string) {
  const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
  if (shouldUseMemberId) return userKey ? `${userKey}.${SEARCH_STORAGE_KEYS.REMOVED_RECENT_SEARCHES}` : undefined
  return userKey ? `${userKey}.${SEARCH_STORAGE_KEYS.REMOVED_RECENT_SEARCHES}` : SEARCH_STORAGE_KEYS.REMOVED_RECENT_SEARCHES
}

export function getRemovedRecentSearchDedupeKeys(userKey?: string): Array<string> {
  const key = getRemovedRecentSearchesStorageKey(userKey)
  if (!key) return emptyStringArray
  return getLocalStorage(key) as Array<string> ?? emptyStringArray
}

function saveRecentSearches(searches: Array<App.FullRecentSearch>, userKey?: string) {
  const key = getRecentSearchesStorageKey(userKey)
  if (!key) return
  setLocalStorage(key, searches)
}

function saveRemovedRecentSearchDedupeKeys(recentSearchDedupeKeys: Array<string>, userKey?: string) {
  const key = getRemovedRecentSearchesStorageKey(userKey)
  if (!key) return
  setLocalStorage(key, recentSearchDedupeKeys)
}

function removeFromDeletedRecentSearches(recentSearchDedupeKey: string, userKey?: string) {
  const nextSearchDedupeKeys = getRemovedRecentSearchDedupeKeys(userKey).filter(id => id !== recentSearchDedupeKey)
  saveRemovedRecentSearchDedupeKeys(nextSearchDedupeKeys, userKey)
}

function addToDeletedRecentSearches(recentSearchDedupeKey: string, userKey?: string) {
  const nextSearchDedupeKeys = unique([recentSearchDedupeKey, ...getRemovedRecentSearchDedupeKeys(userKey)])
  saveRemovedRecentSearchDedupeKeys(nextSearchDedupeKeys, userKey)
}

function mapSearchOccupancyToOccupants(occupancy: App.SearchOccupancy): App.Occupants {
  return {
    adults: occupancy.numberOfAdults,
    children: occupancy.agesOfChildren.length,
    childrenAge: occupancy.agesOfChildren,
  }
}

function buildUserRecentSearchDedupeKey(userRecentSearch: App.UserRecentSearch): string {
  const ids = userRecentSearch.type === 'property' ? userRecentSearch.propertyIds ?? [] : userRecentSearch.placeIds ?? []
  return ids.join(',')
}

export function buildRecentSearchDedupeKey(recentSearch: App.FullRecentSearch): string {
  return recentSearch.searchItem.value
}

function sortRecentSearchesByDateAdded(searches: Array<App.FullRecentSearch>): Array<App.FullRecentSearch> {
  return searches.toSorted((a, b) => {
    const aTime = Date.parse(a.dateAdded)
    const bTime = Date.parse(b.dateAdded)
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
  })
}

function mapUserRecentSearchToFullRecentSearch(userRecentSearch: App.UserRecentSearch): App.FullRecentSearch | undefined {
  const id = userRecentSearch.type === 'property' ? userRecentSearch.propertyIds?.[0] : userRecentSearch.placeIds?.[0]
  if (!id) return undefined

  const searchItem: App.SearchDestination | App.SearchProperty = {
    searchType: userRecentSearch.type === 'property' ? 'property' : 'destination',
    value: id,
    format: {
      mainText: userRecentSearch.query,
    },
  }
  const travelDates = userRecentSearch.travelDates
  const dates: App.SearchDates = {
    checkIn: travelDates.type === 'specific' ? travelDates.checkIn : undefined,
    checkOut: travelDates.type === 'specific' ? travelDates.checkOut : undefined,
    flexibleDuration: travelDates.type === 'flexible' ? travelDates.flexibleDuration : undefined,
    flexibleMonths: travelDates.type === 'flexible' ? travelDates.flexibleMonths : undefined,
    isAnytime: travelDates.type === 'anytime',
  }
  const rooms = userRecentSearch.occupancy.map(mapSearchOccupancyToOccupants)

  return {
    searchItem,
    dates,
    rooms,
    recentSearchId: buildRecentSearchId(dates, searchItem, rooms),
    dateAdded: userRecentSearch.timestamp,
  }
}

export function getRecentSearches(userKey?: string): Array<App.FullRecentSearch> {
  // we currently only store recent searches for a user, this may change but for now return empty if not provided
  const key = getRecentSearchesStorageKey(userKey)
  if (!key) {
    return emptyArray
  }

  let recentSearches = getLocalStorage(key) as Array<App.FullRecentSearch> ?? emptyArray

  // if there aren't any recent searches with the *new* key, try to load using the old key
  if (recentSearches.length === 0 && userKey) {
    recentSearches = getLocalStorage(userKey) as Array<App.FullRecentSearch> ?? emptyArray
  }
  const removedSearchDedupeKeys = new Set(getRemovedRecentSearchDedupeKeys(userKey))
  return sortRecentSearchesByDateAdded(
    clearSearchDatesIfInPast(recentSearches).filter(search => !removedSearchDedupeKeys.has(buildRecentSearchDedupeKey(search))),
  )
}

export function hydrateRecentSearchesFromUserRecentSearches(
  userRecentSearches: Array<App.UserRecentSearch>,
  userKey?: string,
): Array<App.FullRecentSearch> {
  const removedSearchDedupeKeys = new Set(getRemovedRecentSearchDedupeKeys(userKey))
  const recentSearchesWithDedupeKeys = userRecentSearches.flatMap((userRecentSearch) => {
    const dedupeKey = buildUserRecentSearchDedupeKey(userRecentSearch)
    const recentSearch = mapUserRecentSearchToFullRecentSearch(userRecentSearch)
    return recentSearch && dedupeKey ? [{ dedupeKey, recentSearch }] : []
  })
  const recentSearches = uniqueBy(
    recentSearchesWithDedupeKeys,
    search => search.dedupeKey,
  ).map(search => search.recentSearch)
  const nextSearches = take(
    clearSearchDatesIfInPast(recentSearches).filter(search => !removedSearchDedupeKeys.has(buildRecentSearchDedupeKey(search))),
    RECENT_SEARCHES_LIMIT,
  )

  saveRecentSearches(nextSearches, userKey)
  return nextSearches
}

export function saveRecentSearch(
  searchItem: App.SearchItem,
  dates: App.SearchDates,
  rooms: Array<App.Occupants>,
  userKey?: string,
) {
  const newSearchItem = {
    searchItem,
    dates,
    rooms,
    recentSearchId: buildRecentSearchId(dates, searchItem, rooms),
    dateAdded: new Date().toISOString(),
  }

  if (!getRecentSearchesStorageKey(userKey)) {
    // guess they had no member id and we needed, can't save it
    return
  }
  removeFromDeletedRecentSearches(buildRecentSearchDedupeKey(newSearchItem), userKey)
  const allSearches = [newSearchItem, ...getRecentSearches(userKey)]
  const uniqueSearches = uniqueBy(allSearches, search => buildRecentSearchDedupeKey(search))
  const nextSearches = take(uniqueSearches, RECENT_SEARCHES_LIMIT)
  saveRecentSearches(nextSearches, userKey)
}

export function getFormattedDateRange(
  startDate: moment.Moment,
  endDate: moment.Moment,
  intl: IntlShape = getCurrentIntl(),
): string {
  const locale = intl.locale
  if (startDate.month() === endDate.month()) {
    return intl.formatMessage(
      {
        id: '7MkJ7cbRI2X-8S6U8MDb',
        defaultMessage: '{startDay} - {endDate}',
        description: 'Formatted date range for recent search dates when both dates are in the same month',
      },
      {
        startDay: intlDateFormatter(startDate, { day: '2-digit' }, locale),
        endDate: intlDateFormatter(endDate, { day: '2-digit', month: 'short' }, locale),
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'cpJ4YEeturoUFJ7SI47j',
      defaultMessage: '{startDate} - {endDate}',
      description: 'Formatted date range for recent search dates when the dates are in different months',
    },
    {
      startDate: intlDateFormatter(startDate, { day: '2-digit', month: 'short' }, locale),
      endDate: intlDateFormatter(endDate, { day: '2-digit', month: 'short' }, locale),
    },
  )
}

// To remove dates if they are in the past
export function clearSearchDatesIfInPast(searches: Array<App.FullRecentSearch>) {
  return searches.map((search) => {
    if (Object.keys(search.dates).length === 0 || (search.dates.checkIn && !isTodayBeforeCheckin(search.dates.checkIn)) || (search.dates.flexibleMonths && !isTodayBeforeFlexibleMonth(search.dates.flexibleMonths))) {
      const dates: App.SearchDates = { checkIn: undefined, checkOut: undefined, flexibleDuration: undefined, flexibleMonths: undefined, isAnytime: true }
      const recentSearchId = buildRecentSearchId(dates, search.searchItem, search.rooms)
      return { ...search, dates, recentSearchId }
    }
    return search
  })
}

export function deleteRecentSearch(recentSearchId: string, userKey?: string) {
  const searches = getRecentSearches(userKey)
  if (!getRecentSearchesStorageKey(userKey)) {
    // needs a key to work
    return
  }
  const removedSearch = searches.find(search => search.recentSearchId === recentSearchId)
  if (removedSearch) {
    addToDeletedRecentSearches(buildRecentSearchDedupeKey(removedSearch), userKey)
  }
  const nextSearches = searches.filter(search => search.recentSearchId !== recentSearchId)
  saveRecentSearches(nextSearches, userKey)
}

// Helper function for getting unique searches
export function buildRecentSearchId(dates: App.SearchDates, searchItem: App.SearchItem, rooms: Array<App.Occupants>): string {
  return `${dates?.checkIn}-${dates?.checkOut}-${dates?.flexibleDuration}-${dates?.flexibleMonths}-${searchItem.format.mainText}-${searchItem.searchType}-${formatOccupantsAsGuests(...rooms)}`
}

export function prettyDates(intl: IntlShape, checkinDate?: moment.Moment, checkoutDate?: moment.Moment): string {
  if (checkinDate && checkoutDate) {
    const locale = intl.locale
    let formattedCheckIn = intlDateFormatter(checkinDate, INTL_DAY_ONLY, locale)
    const formattedCheckOut = intlDateFormatter(checkoutDate, INTL_DMY_CASUAL_SHORT_FORMAT, locale)

    if (checkinDate.year() === checkoutDate.year()) {
      if (checkinDate.month() !== checkoutDate.month()) {
        formattedCheckIn = intlDateFormatter(checkinDate, INTL_DAY_MONTH_NAME_SHORT, locale)
      }
    } else {
      formattedCheckIn = intlDateFormatter(checkinDate, INTL_DMY_CASUAL_SHORT_FORMAT, locale)
    }

    return intl.formatMessage(
      {
        id: 'Unc93FTTk6bw1TWehP6D',
        defaultMessage: '{checkInDate} - {checkOutDate}',
        description: 'Pretty date range shown in hotel search when check-in and check-out dates are selected',
      },
      {
        checkInDate: formattedCheckIn,
        checkOutDate: formattedCheckOut,
      },
    )
  }
  return intl.formatMessage({
    id: 'nEK4H-jsvrG9E8Ppwi5Z',
    defaultMessage: 'Anytime',
    description: 'Label for flexible date search with no specific dates in pretty dates',
  })
}

function getDateIn30Days() {
  const date = new Date()
  date.setDate(date.getDate() + 30)
  return date.getTime()
}

export function getPageTitle(
  intl: IntlShape,
  searchItem: App.SearchItem | undefined,
  dates: {
    checkIn?: moment.Moment
    checkOut?: moment.Moment
    flexibleNights?: FLEXIBLE_DURATION_RANGE
    flexibleMonths?: string
  }): string {
  const { checkIn, checkOut, flexibleNights, flexibleMonths } = dates
  const searchText = searchItem?.format.mainText
  const anytimeLabel = intl.formatMessage({
    id: 'Cde9fVjtwU0urKObGvT-',
    defaultMessage: 'Anytime',
    description: 'Label for anytime date selection in search page title',
  })
  let dateString = anytimeLabel
  const tagline = intl.formatMessage({
    id: 'e3xD4tgTQ2SY3AmEs_J9',
    defaultMessage: 'Luxury Escapes: Handpicked Escapes at the Best Prices',
    description: 'Brand tagline appended to search page title in the search interface',
  })
  if (checkIn && checkOut) {
    dateString = prettyDates(intl, checkIn, checkOut)
  } else if (flexibleNights || flexibleMonths) {
    dateString = prettyFlexibleDates(flexibleMonths, flexibleNights, undefined, intl)
  }
  const hotelsLabel = intl.formatMessage(productLabels.hotels)
  let resultString = `${searchText}`
  resultString += searchItem?.searchType === 'property' ? '' : ` ${hotelsLabel}`
  resultString += dateString !== anytimeLabel ? `: ${dateString}` : ''
  resultString = intl.formatMessage(
    {
      id: 'xH-bfvN0UYezo5OOQWOA',
      defaultMessage: '{title} · {tagline}',
      description: 'Search page title with brand tagline suffix in the search interface',
    },
    {
      title: resultString,
      tagline,
    },
  )
  return resultString
}

export function getRecentFilters(): RecentFilters {
  const recentFilters = getLocalStorage(SEARCH_STORAGE_KEYS.RECENT_FILTERS)
  if (recentFilters?.expiryDate && Number(recentFilters.expiryDate) > new Date().getTime()) {
    if (recentFilters.customerRatingGte) {
      recentFilters.customerRatingGte = Number(recentFilters.customerRatingGte)
    }
    recentFilters.expiryDate = Number(recentFilters.expiryDate)
    return recentFilters
  }
  return {
    holidayTypes: [],
    propertyTypes: [],
    amenities: [],
    inclusions: [],
    locations: [],
    expiryDate: getDateIn30Days(),
    orderBy: [],
    bedroomsEq: undefined,
    bedsEq: undefined,
    bathroomsEq: undefined,
    luxPlusFeatures: [],
    campaigns: [],
    agentHubFeatures: [],
  }
}

export function saveRecentFilters(filters: Partial<RecentFilters>) {
  const recentFilters = getRecentFilters()
  for (const _key in filters) {
    const key = _key as keyof RecentFilters
    if (filters[key]) {
      if (key === 'customerRatingGte') {
        recentFilters[key] = filters[key]
        recentFilters.orderBy = unique([filters[key], ...(recentFilters.orderBy ?? [])])
      } else if (key !== 'orderBy' && key !== 'expiryDate' && key !== 'bedroomsEq' && key !== 'bedsEq' && key !== 'bathroomsEq') {
        recentFilters[key] = take(unique(filters[key].concat(...(recentFilters[key] ?? []))), 20)
        recentFilters.orderBy = unique([...filters[key], ...(recentFilters.orderBy ?? [])])
      }
    }
  }
  recentFilters.expiryDate = getDateIn30Days()
  setLocalStorage(SEARCH_STORAGE_KEYS.RECENT_FILTERS, recentFilters)
}

export function getAppliedFilterCount(filters: App.OfferListFilters, enabledFilters: EnabledFilters, excludeOfferTypes = false): number {
  const hasBedroomFilter = !!(filters.bedroomsGte || filters.bedroomsEq)
  const hasBedFilter = !!(filters.bedsGte || filters.bedsEq)
  const hasBathroomFilter = !!(filters.bathroomsGte || filters.bathroomsEq)

  return (enabledFilters.holidayTypes ? filters.holidayTypes?.length ?? 0 : 0) + (filters.amenities?.length ?? 0) +
    (filters.inclusions?.length ?? 0) + (filters.propertyFacilities?.length ?? 0) +
    (filters.roomAmenities?.length ?? 0) + (filters.bedTypes?.length ?? 0) +
    (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0) +
    (excludeOfferTypes ? 0 : (filters.offerTypes?.length === 1 ? 1 : 0)) + (hasBedroomFilter ? 1 : 0) + (hasBedFilter ? 1 : 0) + (hasBathroomFilter ? 1 : 0) + (filters.luxPlusFeatures?.length ?? 0) +
    (enabledFilters.campaigns ? filters.campaigns?.length ?? 0 : 0) +
    (!enabledFilters.campaigns && filters.campaigns?.includes(WORLD_TRAVEL_DAY_2026_CAMPAIGN_SLUG) ? 1 : 0)
}

export function getIsEnabledFilterKey(key: string): key is keyof EnabledFilters {
  return key in HOTELS_ENABLED_FILTERS
}

export function formatSearchItemSubtitle(intl: IntlShape, searchItem: App.FullRecentSearch, includeRooms = true) {
  const { dates, rooms } = searchItem

  const dateText = getRecentSearchDescription(intl, dates.checkIn, dates.checkOut, dates.flexibleMonths, dates.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE, dates.isAnytime)

  if (!!dateText && !includeRooms) {
    return dateText
  }

  const occupancyText = formatOccupantsAsGuests(...rooms)

  if (dateText === '' || occupancyText === '') return ''

  const roomsText = intl.formatMessage(
    {
      id: 'g7zyI_NLqO29bH-3rIri',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count in recent search suggestion subtitle in the search interface',
    },
    { count: rooms.length },
  )
  return intl.formatMessage(
    {
      id: 'AkD77GC8j9e2YN5rX63d',
      defaultMessage: '{dateText} · {occupancyText} · {roomsText}',
      description: 'Recent search suggestion subtitle with dates, occupancy, and room count',
    },
    {
      dateText,
      occupancyText,
      roomsText,
    },
  )
}

export function isOfTypeLuxPlusFeatureFilter(feature: string): boolean {
  const luxPlusFeatureFilters: Array<string> = [
    'Early access', 'Member prices', 'Bonus inclusions', 'Member exclusive',
    'early_access', 'member_prices', 'bonus_inclusions', 'member_exclusive',
  ]

  return luxPlusFeatureFilters.includes(feature)
}

const LOYALTY_FEATURE_FILTERS = new Set(LOYALTY_SEARCH_FILTER_VALUES)
export function isOfTypeLoyaltyFeatureFilter(feature: string): boolean {
  return LOYALTY_FEATURE_FILTERS.has(feature)
}

export function getLuxPlusFilterDisplayName(intl: IntlShape, filter: string): string {
  switch (filter) {
    case 'early_access':
    case 'Early access':
      return intl.formatMessage({
        id: '_lQ-OZMhHCBzah2llCGc',
        defaultMessage: 'LuxPlus+ Early access',
        description: 'Display label for the LuxPlus early access filter chip on hotel search',
      })
    case 'member_prices':
    case 'Member prices':
      return intl.formatMessage({
        id: 'UE357lkVeUIzoBqeyc5X',
        defaultMessage: 'LuxPlus+ Member prices',
        description: 'Display label for the LuxPlus member prices filter chip on hotel search',
      })
    case 'bonus_inclusions':
    case 'Bonus inclusions':
      return intl.formatMessage({
        id: 'ZIwZnfAcXKTxAP_sS4Hg',
        defaultMessage: 'LuxPlus+ Bonus inclusions',
        description: 'Display label for the LuxPlus bonus inclusions filter chip on hotel search',
      })
    case 'member_exclusive':
    case 'Member exclusive':
      return intl.formatMessage({
        id: 'h_CSx2LTGguPPVRWrpN8',
        defaultMessage: 'LuxPlus+ Member exclusive',
        description: 'Display label for the LuxPlus member exclusive filter chip on hotel search',
      })
    default:
      return intl.formatMessage(
        {
          id: 'BX4wdEZTcSUVyZjQQx25',
          defaultMessage: 'LuxPlus+ {filterName}',
          description: 'Fallback display label for a LuxPlus hotel search filter chip when the feature name is unknown',
        },
        { filterName: filter },
      )
  }
}

export function getAgentHubExclusiveFilterDisplayName(intl: IntlShape, filter: string): string {
  if (filter === 'agent_hub_exclusive' || filter === 'Agent Hub Exclusive') {
    return intl.formatMessage({
      id: 'D32nYriXyzqNBxZ4wuzj',
      defaultMessage: 'Agent Hub Exclusive',
      description: 'Display label for the Agent Hub exclusive filter chip on hotel search',
    })
  }

  return filter
}

function extractBedbankId(value: string | undefined): string | undefined {
  const bedbankPrefix = 'bedbank:'
  if (value?.startsWith(bedbankPrefix)) {
    return value.substring(bedbankPrefix.length)
  }
  return undefined
}

export function getOfferIdFromSearchItem(searchItem: App.SearchItem): string | undefined {
  const experienceId = getSearchItemExperienceId(searchItem)
  if (experienceId) return experienceId

  // V1 property search item
  if (searchItem.searchType === 'property') {
    return searchItem.offerId ?? extractBedbankId(searchItem.value)
  }

  // V3 search item
  if (searchItem.searchType === 'searchItemV3') {
    if (searchItem.display.offerId) return searchItem.display.offerId

    const { extractedData } = searchItem.searchItem
    if (extractedData.searchType === 'property' && extractedData.property?.propertyType === 'bedbank') {
      return extractedData.property.propertyId
    }
  }

  return undefined
}

export function isFlexibleMonthsWithinDealDates(
  flexibleMonths: string,
  dealStart: moment.Moment,
  dealEnd: moment.Moment,
): boolean {
  return flexibleMonths.split(',').some((month: string) => {
    const startOfMonth = moment(`${month}-01`, 'YYYY-MM-DD')
    const endOfMonth = startOfMonth.clone().endOf('month')

    const currentDay = startOfMonth.clone()
    while (currentDay.isSameOrBefore(endOfMonth, 'day')) {
      if (currentDay.isBetween(dealStart, dealEnd, null, '[]')) {
        return true
      }
      currentDay.add(1, 'day')
    }
    return false
  })
}

export function isMetaSearchPage(pathname: string) {
  return pathname.includes('/metasearch')
}

export function isAgentHubExclusiveFilter(filter: string): boolean {
  return filter === 'agent_hub_exclusive' || filter === 'Agent Hub Exclusive'
}

/**
 * Calculates the discount percentage for search menu list item offers
 */
export function getSearchMenuListItemDiscount(searchItem: App.SearchItem, offer?: App.AnyOffer): number | undefined {
  const isPropertyItem =
    (searchItem.searchType === 'property') ||
    (searchItem.searchType === 'searchItemV3' && searchItem.display?.searchType === 'property')

  if (!offer) {
    return undefined
  }

  if (isExperienceOffer(offer)) {
    return offer?.discount
  }

  if (!isPropertyItem) {
    return undefined
  }

  if (isLEHotel(offer)) {
    const value = offer?.lowestPricePackage?.value ?? 0
    const price = offer?.lowestPricePackage?.price ?? 0
    return getValueOffPercent(value, price)
  }

  if (isBedbankOffer(offer)) {
    return offer?.sell?.discountPercentage
  }

  return undefined
}
